<template>
  <div class="gw-completed f-h">
    <FiltersZone :modal-title="$t('message.OPTIONS')">
      <div slot="shortcuts" class="gw-completed__subheader">
        <Switcher v-model="viewMode" :titles="viewModes" />
        <!-- GW List Menu -->
        <Dropdown
          :key="compGembawalk ? compGembawalk.id : 1"
          class="gw-completed__dropdown"
          :closed-text="currentGwOccurence"
          small
        >
          <RouterLink
            v-for="gw in gwList"
            :key="gw.id"
            class="list__tile list__tile--link"
            :to="{ path: '/gw-completed/' + gw.id, query: $route.query.shizenStrip(['last']) }"
            replace
          >
            <div class="list__tile__title">
              {{ gw.dateMoment.format($t("dateFormat")) }}
            </div>
          </RouterLink>
        </Dropdown>
        <button
          class="gw-completed__participant editable-label-text generic-border--small-text-btn"
          @click="openTeamRecapModal(compGembawalk)"
        >
          <Icon>{{ teamMedium }}</Icon>
          <span>{{ $t("message.COMPLETED_GW_SEE_PARTICIPANTS") }}</span>
        </button>
      </div>

      <div>
        <button
          class="generic-border generic-border--text-btn generic-border--blue"
          @click="exportGwToExcel(compGembawalk, $store.getters.companyUsers)"
        >
          <Icon>{{ downloadMedium }}</Icon>
          <span>{{ $t('message.EXPORT_TO_EXCEL') }}</span>
        </button>
        <h2 v-if="ratings && ratings.length" class="basic-sub-title">
          {{ $t("message.GW_REVIEWS") }}
        </h2>
        <div v-for="rating in ratings" :key="rating.id" class="mt10">
          <div class="legend">
            {{ rating.teamName }}
          </div>
          <div class="rating-grades mt5">
            <div>
              <span>{{ $t('message.GW_MOOD') + $t('message.TWO_DOTS') }}</span>
              <GwRater v-model="rating.ratingMood" :disabled="true" :small="true" />
            </div>
            <div>{{ $t('message.COMMENTS') + $t('message.TWO_DOTS') }}</div>
            <div class="review-comment">
              {{ rating.commentMood }}
            </div>
          </div>
          <div class="rating-grades mt5">
            <div>
              <span>{{ $t('message.GW_RELEVANCE') + $t('message.TWO_DOTS') }}</span>
              <GwRater v-model="rating.ratingRelevance" :disabled="true" :small="true" />
            </div>
            <div>{{ $t('message.COMMENTS') + $t('message.TWO_DOTS') }}</div>
            <div class="review-comment">
              {{ rating.commentRelevance }}
            </div>
          </div>
        </div>
      </div>
    </FiltersZone>
    <!-- Summary view -->
    <div v-if="viewMode == 0" class="f-h-below-filters gw-synthesis">
      <div class="gw-completed__global-graph">
        <h3 class="legend--bottom-margin">
          {{ $t('message.GW_OVERALL_STATS') }}
        </h3>
        <GwLineChart v-if="globalStepsChart" :values="globalStepsChart" />
      </div>
      <div
        v-for="(step, stepIndex) in compGembawalk ? compGembawalk.steps : []"
        :key="step.id"
        class="gw-completed__expendable-step"
      >
        <ExpandableSettingsItem ref="stepItems" :title="decodeHTML(step.name)" use-transition-expand>

          <div v-if="compGembawalk.shouldSetAuditedUser === 1" class="gw-completed__expendable-step__audited-user">
            <div class="legend legend--bottom-margin">
              {{ $t('message.GW_AUDITED_USER') }}
            </div>
            <div class="user-edition">
              <UserThumbnail
                :value="step.auditedUser"
                :all-users="$store.getters.usersPerTeam[`${currentStep.auditedTeam}`]"
                fetch-data
                disabled
              />
              <EditableLabelRadio
                :value="step.auditedUser"
                :items="$store.getters.usersPerTeam[`${step.auditedTeam}`]"
                :placeholder="$t('message.GW_AUDITED_USER')"
                disabled
                generic-border
              />
            </div>

            <div class="gw-ongoing__content__current__wrapper__sep" />
          </div>

          <GwLineChart :values="globalStepsChart" :step-id="step.id" />
          <GwChecklistsWrapper
            :gw="compGembawalk"
            :step-index="stepIndex"
            :check-lists="step.checklists"
            :disabled="!canEdit"
            @edit="checklistsWrapperEdit"
          />

          <ExpandableSettingsItem
            :ref="`comments${step.id}`"
            :title="`${$t('message.GW_STEP_COMMENTS')} (${step.comments.length})`"
            class="summary-comments"
            :default-expand="step.comments.length !== 0"
            use-transition-expand
          >
            <GwCommentsWrapper
              :gw="compGembawalk"
              :step-id="step.id"
              :comments="step.comments"
              pb-button
            />
          </ExpandableSettingsItem>
        </ExpandableSettingsItem>
      </div>
    </div>
    <!-- End of summary view -->

    <!-- Detailed view (steps, problems, actions) -->
    <div
      v-if="viewMode === 1"
      class="gw-completed__details f-h-below-filters"
      :column-mode="isSmPortrait"
      @input="$refs.steps ? $refs.steps.updateNames : null"
    >
      <div class="gw-completed__steps f-h">
        <h2 class="basic-sub-title">
          {{ $t("message.GW_SUMMARY") }}
        </h2>
        <GwStepsNavigator
          ref="steps"
          :gw="compGembawalk"
          :index="currentStepIndex"
          :completed="true"
          :disabled="!canEdit"
          @stepChange="stepChanged"
          @reportProblem="addProblem"
          @edit="sendSocketUpdate"
        />
      </div>
      <div class="gw-completed__right-panel">
        <div class="gw-completed__problems generic-border--card">
          <h2 class="basic-sub-title">
            {{ $t("message.PROBLEMS") }}
          </h2>
          <Dropdown :closed-text="problemDisplayChoice === 0 ? problemsDisplay[0].label : problemsDisplay[1].label">
            <RadioGroup v-model="problemDisplayChoice" :items="problemsDisplay" />
          </Dropdown>
          <h3>
            {{ $tc('message.GW_STEPS_PROBLEMS', problemDisplayChoice ? 1 : 2) }}
          </h3>
          <div v-if="stepsDispProblems.length === 0" class="no-problems-message">
            {{ $t('message.GW_NO_PROBLEMS_STEP') }}
          </div>
          <ProblemItem
            v-for="pb in stepsDispProblems"
            :key="pb.id"
            :problem="pb"
            @removed="problemDeleted(pb)"
          />

          <div
            v-show="canEdit"
            class="
              generic-border
              generic-border--text-btn
              generic-border--gray-border
              gw-completed__problems__add-problem
            "
            @click="addProblem"
          >
            <div class="generic-border__left">
              <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
              <span>{{ $t('message.REPORT_PROBLEM') }}</span>
            </div>
            <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
          </div>

          <h3 v-if="checklistsDispProblems.length > 0">
            {{ $tc('message.GW_STEPS_CL_PROBLEMS', problemDisplayChoice ? 1 : 2) }}
          </h3>
        </div>
        <div class="gw-completed__actions generic-border--card">
          <h2 class="basic-sub-title">
            {{ $t("message.ACTIONS") }}
          </h2>
          <Dropdown :closed-text="currentActionDisplayChoice">
            <RadioGroup
              v-model="actionsDisplayChoice"
              :items="actionsDisplay"
              @input="actionChoiceMade"
            />
          </Dropdown>
          <EditableLabelRadio
            ref="apChoice"
            v-model="actionsDisplayPbId"
            :items="displayedProblems"
            label-key="lWhat"
            no-text
            @input="actionPbChoiceMade"
            @close="actionPbChoiceMade"
          />
          <PendingActionItem
            v-for="(action, aIndex) in displayedActions"
            :key="action.id"
            :data-action-idx="aIndex"
            :value="action"
            no-drag-handle
            @actionClick="sheetClicked(action)"
            @deleteClick="askDeleteAction(aIndex, action)"
          />

          <div
            v-show="canEdit"
            class="
              generic-border
              generic-border--text-btn
              generic-border--gray-border
              gw-completed__actions__add-action
            "
            @click="addAction"
          >
            <div class="generic-border__left">
              <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
              <span>{{ $t('message.ADD_AN_ACTION') }}</span>
            </div>
            <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
          </div>
        </div>
      </div>
    </div>
    <AdvancedTeamPicker
      v-if="displayTeamPicker"
      :value="[]"
      class="italic info-teams"
      legend=""
      ok-button
      invisible
      edit-mode-always-on
      single-value
      @input="teamPicked"
    />

    <!-- Refresh popup -->
    <RefreshPrompt
      ref="refPrompt"
      :title="$t('message.GW_WAS_EDITED_REFRESH')"
      @refresh="refresh"
    />

    <!-- Intrusive help for covid version -->
    <IntrusiveHelp
      :displayable="!!currentTeam.covidVersion && !!compGembawalk.id"
      help-key="GwCompleted"
      @triggerComponentHelp="triggerComponentHelp"
    />
    <!-- Team recap modal -->
    <GwTeamRecapModal
      v-if="modalContent"
      :modal-content="modalContent"
      :visible="participatingTeamsModal"
      @toggleModal="toggleModal()"
    />
  </div>
</template>

<script>
/**
 * Gemba walk completed page
 *
 * @vue-prop {Number} gembaWalkId - ID of the gemba walk information to display
 * @vue-prop {Boolean} embed - Is the component embed or an independent view (will remove close button)
 *
 * @vue-computed {String} pageTitle - Head title
 * @vue-computed {String} dispDate - Display date of this GW
 * @vue-computed {String} currentStep - Step object of the currently selected step
 * @vue-computed {Array} displayedProblems - List of problems to show according to selected options
 * @vue-computed {Array} displayedActions - List of actions to show according to selected options
 * @vue-computed {Array} problemsDisplay - List of available choices to display problems
 * @vue-computed {Array} actionsDisplay - List of available choices to display actions
 * @vue-computed {Boolean} canEdit - Is the current user allowed to edit the GW
 */

import toastr from 'toastr';
import { cloneDeep, isEqual } from 'lodash';
// Vuex utils
import { FETCH_GW, TICK_ITEM } from '@/store/modules/gemba-walks/action-types';
// Import components
import AdvancedTeamPicker from '@/components/advanced-team-picker.vue';
import Dropdown from '@/components/dropdown.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import ExpandableSettingsItem from '@/components/expandable-settings-item.vue';
import FiltersZone from '@/components/filters-zone.vue';
import GwChecklistsWrapper from '@/components/gw-checklists-wrapper.vue';
import GwCommentsWrapper from '@/components/gw-comments-wrapper.vue';
import GwRater from '@/components/gw-rater.vue';
import GwStepsNavigator from '@/components/gw-steps-navigator.vue';
import GwTeamRecapModal from '@/components/gw-team-recap-modal.vue';
import Icon from '@/components/icon.vue';
import IntrusiveHelp from '@/components/intrusive-help.vue';
import GwLineChart from '@/views/gw/gw-line-chart.vue';
import PendingActionItem from '@/components/pending-action-item.vue';
import ProblemItem from '@/components/problem-item.vue';
import RadioGroup from '@/components/radio-group.vue';
import RefreshPrompt from '@/components/refresh-prompt.vue';
import Switcher from '@/components/switcher.vue';
import UserThumbnail from '@/components/user-thumbnail.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Import utils
import { addExtraBold, arrowDownLine, downloadMedium, teamMedium } from '@/assets/icons';
import IAHelp from '@/utils/classes/IAHelp';
import IAHelpStep from '@/utils/classes/IAHelpStep';
import { calert, decodeHTML } from '@/utils/main';
import {
  actionCreationQueryParam,
  ongoingGwQueryParam,
  pbSheetQueryParam,
} from '@/utils/navigation-constants';
import { exportGwToExcel } from '@/utils/gw-utils';

export default {

  name: 'GwCompleted',

  components: {
    AdvancedTeamPicker,
    Dropdown,
    EditableLabelRadio,
    ExpandableSettingsItem,
    FiltersZone,
    GwChecklistsWrapper,
    GwCommentsWrapper,
    GwRater,
    GwStepsNavigator,
    GwTeamRecapModal,
    Icon,
    IntrusiveHelp,
    GwLineChart,
    PendingActionItem,
    ProblemItem,
    RadioGroup,
    RefreshPrompt,
    Switcher,
    UserThumbnail,
  },

  mixins: [mixinSocketHandler],

  props: {
    gembaWalkId: {
      type: [Number, String], // Silence warning
      default: 0,
      validator: val => !isNaN(val),
    },
    embed: Boolean,
  },

  data() {
    return {
      // Icons
      addExtraBold,
      arrowDownLine,
      downloadMedium,
      teamMedium,
      // Synthesis and detailed modes
      viewModes: [this.$t('message.GW_SYNTHESIS'), this.$t('message.GW_DETAILED')],
      // Detailed view data
      afterOngoing: false,
      gembaWalk: {},
      demoGw: {},
      currentStepIndex: 0,
      problemDisplayChoice: 1,
      actionsDisplayChoice: 1,
      prevActionDispChoice: 1,
      actionsDisplayProblem: null,
      actionsDisplayPbId: 0,
      displayTeamPicker: false,
      gwList: [],
      // Ratings from audited teams reviews
      ratings: [],
      // Modal data
      modalContent: null,
      // Properties used by other components. See src/views/readme.md
      pageBackButtonAction: () => {
        if (this.afterOngoing) {
          // Don't go back to ongoing GW, go to completed GW
          this.$router.replace('/gemba-walks');
        } else {
          this.$router.back();
        }
      },
    };
  },

  computed: {
    /** Object: GW Object from the state */
    stateGembaWalk() {
      return this.$store.state.gembaWalks.gwPerId[`${this.gembaWalkId}`];
    },
    /** Head title
     * Properties used by other components. See src/views/readme.md
     */
    pageTitle() {
      return (this.compGembawalk && this.compGembawalk.name)
        ? decodeHTML(this.compGembawalk.name)
        : this.$t('message.LOADING');
    },
    /** Display date of this GW */
    dispDate() {
      if (!this.compGembawalk) {
        return '';
      }
      return this.$moment(this.compGembawalk.date).format(this.$t('dateFormat'));
    },
    /** Steps can be focused one at a time */
    currentStep() {
      if (this.compGembawalk && this.compGembawalk.steps && this.compGembawalk.steps.length) {
        return this.compGembawalk.steps[this.currentStepIndex];
      }
      return { name: '', prevComments: [] };
    },
    /** Problems to show according to selected options */
    displayedProblems() {
      return this.stepsDispProblems.concat(this.checklistsDispProblems);
    },
    checklistsDispProblems() {
      if (this.problemDisplayChoice === 1 || !this.compGembawalk.steps) {
        return this.stepChecklistProblems(this.currentStep);
      }
      const problems = [];
      for (let i = 0, len = this.compGembawalk.steps.length; i < len; i++) {
        problems.push(...this.stepChecklistProblems(this.compGembawalk.steps[i]));
      }
      return problems;
    },
    stepsDispProblems() {
      if (this.problemDisplayChoice === 1 || !this.compGembawalk.steps) {
        return this.stepProblems(this.currentStep) || [];
      }
      const problems = [];
      for (let i = 0, len = this.compGembawalk.steps.length; i < len; i++) {
        problems.push(...this.stepProblems(this.compGembawalk.steps[i]));
      }
      return problems;
    },
    /** Actions to show according to selected options */
    displayedActions() {
      if (this.actionsDisplayChoice === 1 || !this.compGembawalk.steps) {
        return this.currentStep.actions || [];
      }
      let actions = [];
      if (this.actionsDisplayChoice === 2 && this.actionsDisplayProblem != null) {
        const { id } = this.actionsDisplayProblem;
        let pbStep = this.currentStep;
        if (Number(this.currentStep.id) !== Number(this.actionsDisplayProblem.gwStep)) {
          // Find this problem's step
          pbStep = this.getStep(this.actionsDisplayProblem.gwStep);
        }
        for (let i = 0, len = pbStep.actions.length; i < len; i++) {
          if (Number(pbStep.actions[i].problem) === Number(id)) {
            actions.push(pbStep.actions[i]);
          }
        }
        return actions;
      }
      for (let i = 0, len = this.compGembawalk.steps.length, step; i < len; i++) {
        step = this.compGembawalk.steps[i];
        if (step.actions && step.actions.length) {
          actions = actions.concat(step.actions);
        }
      }
      return actions;
    },
    /** Available choices to display problems */
    problemsDisplay() {
      return [
        { id: 0, label: this.$t('message.GW_SHOW_ALL_PROBLEMS') },
        { id: 1, label: this.$t('message.GW_SHOW_PROBLEMS_FOR', { step: this.currentStep.name }) },
      ];
    },
    /** Available choices to display actions */
    actionsDisplay() {
      const choices = [
        { id: 0, label: this.$t('message.GW_SHOW_ALL_ACTIONS') },
        { id: 1, label: this.$t('message.GW_SHOW_ACTIONS_STEP', { step: this.currentStep.name }) },
        { id: 2, label: this.$t('message.GW_SHOW_ACTIONS_OF_PB') },
      ];
      if (this.actionsDisplayProblem !== null && this.actionsDisplayChoice === 2) {
        choices[2].label = this.$t('message.GW_SHOW_ACTIONS_PB', { pb: this.actionsDisplayProblem.lWhat });
      }
      return choices;
    },
    /** Is the current user allowed to edit the GW */
    canEdit() {
      return this.gembaWalk ? this.gembaWalk.canEdit : false;
    },
    /** String: JSON encoded data for problem edition */
    pbReportData() {
      const gwStep = this.gembaWalk.steps[this.currentStepIndex];
      return JSON.stringify({
        gwId: this.gembaWalk.id,
        gwName: this.gembaWalk.name,
        stepId: gwStep.id,
        stepName: gwStep.name,
        stepTeam: gwStep.auditedTeam,
        stepZone: gwStep.zone,
      });
    },
    /** Wether this device matches the sm portrait media query */
    isSmPortrait() {
      return this.$store.state.device.isSmartphonePortrait;
    },
    /** Object: GW instance to use on the page */
    compGembawalk() {
      return this.demoMode ? this.demoGw : this.gembaWalk || {};
    },
    // ============
    // Query params
    viewMode: {
      get() {
        return Number(this.$route.query.viewMode || 0);
      },
      set(mode) {
        const query = Object.assign({}, this.$route.query);
        query.viewMode = mode;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    displayRatings: {
      get() {
        return this.$route.query.showRatings === 'true';
      },
      set(display) {
        const query = Object.assign({}, this.$route.query);
        query.showRatings = display ? 'true' : 'false';
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    currentGwOccurence() {
      const { gembaWalkId } = this.$route.params;
      if (this.gwList.length && gembaWalkId) {
        return this.gwList.find(gw => gw.id === Number(gembaWalkId)).dateMoment.format(this.$t('dateFormat'));
      }
      return '';
    },
    globalStepsChart() {
      let ok = 0;
      let nok = 0;
      let na = 0;
      let total = 0;
      const steps = {};
      if (this.compGembawalk && this.compGembawalk.steps) {
        this.compGembawalk.steps.forEach((step) => {
          steps[step.id] = {};
          step.checklists.forEach((checklist) => {
            checklist.items.forEach((item) => {
              total++;
              steps[step.id].total = steps[step.id].total + 1 || 1;
              if (item.ok === 1) {
                steps[step.id].ok = steps[step.id].ok + 1 || 1;
                ok++;
              } else if (item.ok === 0) {
                steps[step.id].nok = steps[step.id].nok + 1 || 1;
                nok++;
              } else if (item.ok === 2) {
                steps[step.id].na = steps[step.id].na + 1 || 1;
                na++;
              }
            });
          });
        });
      } else {
        return false;
      }
      return {
        ok,
        nok,
        na,
        total,
        steps,
      };
    },
    currentActionDisplayChoice() {
      return this.actionsDisplay[this.actionsDisplayChoice].label;
    },
  },

  watch: {
    stateGembaWalk() {
      this.$refs.refPrompt.setRefreshPending(false);
      this.gembaWalk = cloneDeep(this.stateGembaWalk);
    },
    // Triggered on prop/route change
    gembaWalkId(newValue) {
      this.refresh(newValue);
    },
    $route(newValue, old) {
      if (old.query[actionCreationQueryParam] || old.query[pbSheetQueryParam]) {
        this.refresh(this.gembaWalkId);
      }
    },
    isPbReportingDisplayed() {
      // A problem, most likely related to this GW, was updated
      if (!this.isPbReportingDisplayed) {
        this.$store.dispatch(`gembaWalks/${FETCH_GW}`, { id: this.gembaWalkId });
      }
    },
    isPbSheetDisplayed() {
      // A problem, most likely related to this GW, was updated
      if (!this.isPbSheetDisplayed) {
        this.$store.dispatch(`gembaWalks/${FETCH_GW}`, { id: this.gembaWalkId });
      }
    },
  },

  beforeRouteEnter(to, from, next) {
    next((comp) => {
      // If query 'last' is set, get last GW of templateId
      const templateId = Number(to.query.last);
      if (templateId) {
        comp.fetchLastGW(templateId);
      }
      comp.afterOngoing = from.query[ongoingGwQueryParam] > 0;
    });
  },

  created() {
    if (this.gembaWalkId) {
      this.refresh(this.gembaWalkId);
    }
    this.registerSocketHandlers();
  },

  beforeRouteUpdate(to, from, next) {
    // If query 'last' is set, get last GW of templateId
    const templateId = Number(to.query.last);
    if (templateId) {
      this.fetchLastGW(templateId);
    }
    next();
  },

  methods: {
    decodeHTML,
    exportGwToExcel,
    /** Register all socket handlers */
    registerSocketHandlers() {
      this.handleSocketMessage(SocketMessages.CC_MSGS.GW_STEP, (msg) => {
        if (Number(msg.id) !== Number(this.gembaWalk.id)) return;
        this.$refs.refPrompt.setRefreshPending(true);
      });
      this.handleSocketMessage(SocketMessages.CC_MSGS.GW_RATING, (msg) => {
        if (Number(msg.id) !== Number(this.gembaWalk.id)) return;
        this.refreshRatings();
      });
    },
    /** Fetch GW that have the same template - Used for GW list menu */
    fetchGWList(templateId) {
      this.App.post('/server/gembaWalks/get.php', { completed: true, template: templateId }, (d) => {
        if (d instanceof Array) {
          d.forEach((gw) => {
            gw.dateMoment = this.$moment(gw.date);
          });
          this.gwList = d.sort((a, b) => b.dateMoment - a.dateMoment);
        }
      });
    },
    /**
     * Fetch last GW corresponding to a template
     * @param {Number} templateId - ID of the GW template
     */
    fetchLastGW(templateId) {
      // Fetch the gembaWalk from the server
      this.$store.dispatch(`gembaWalks/${FETCH_GW}`, { last: this.gembaWalkId });
      this.fetchGWList(templateId);
    },
    /**
     * Load data of a gembawalk
     * @param {Number} gembaWalkId - Id of the gemba walk
     */
    refresh(gembaWalkId) {
      const gwId = gembaWalkId || (this.gembaWalk && this.gembaWalk.id) || this.gembaWalkId;
      if (!gwId) {
        // No valid ID, don't refresh
        return;
      }
      this.fetchGWById(gwId);
      this.refreshRatings();
    },
    fetchGWById(gwId) {
      this.$store.dispatch(`gembaWalks/${FETCH_GW}`, { id: gwId }).then((d) => {
        this.$refs.refPrompt.setRefreshPending(false);
        // If not completed, redirect to GW ongoing page
        if (d.completed === 0) {
          this.$router.push(this.getGwOngoingLink(d.id));
        } else {
          this.fetchGWList(d.template);
          this.gembaWalk = cloneDeep(d);
        }
      });
    },
    /** Fetch audited users ratings */
    refreshRatings() {
      this.App.post('/server/gembaWalks/getRatings.php', { id: this.gembaWalkId, all: true }, (d) => {
        if (d instanceof Array) {
          this.ratings = d;
        }
      });
    },
    checklistsWrapperEdit() {
      this.sendSocketUpdate();
    },
    /**
     * Refresh the displayed data of a step
     * @param {Number} index - Id of the step
     */
    stepChanged(index) {
      this.currentStepIndex = index;
      this.problemDisplayChoice = 1;
      this.actionsDisplayChoice = 1;
    },
    /**
     * Change a comment into a problem
     * @param comIndex
     * @param com
     */
    addProblem(comIndex, com) {
      this.$http.post('/server/problems', {
        gwId: this.gembaWalk.id,
        gwStep: com ? com.gwStep : this.currentStep.id,
        gwComment: com ? com.id : 0,
      })
        .then(({ data }) => {
          if (data instanceof Object && data.id) {
            const step = this.getStep(data.gwStep);
            step.problems.push(data);
            if (typeof com.id !== 'undefined' && !isNaN(com.id)) {
              step.comments = step.comments.filter(comment => comment.id !== com.id);
            }
            this.sendSocketUpdate();
          } else {
            toastr.error(this.$t('message.CONNECTION_ERROR'));
          }
        });
    },
    /** Problem has been deleted */
    problemDeleted(problem) {
      const id = Number(problem.id);
      const gwClItem = Number(problem.gwClItem);
      let parent;
      // Splice either from step > problems OR checklist > item > problems
      if (!gwClItem) {
        parent = this.gembaWalk.steps.find(
          step => step.problems.findIndex(pb => Number(pb.id) === id) > -1,
        );
      } else {
        this.gembaWalk.steps.find(
          step => !!(step.checklists || []).find(
            // eslint-disable-next-line no-return-assign
            cl => parent = cl.items.find(item => Number(item.id) === gwClItem),
          ),
        );
      }
      if (parent) {
        const pbIndex = parent.problems.findIndex(pb => Number(pb.id) === id);
        if (pbIndex > -1) {
          parent.problems.splice(pbIndex, 1);
        }
      }
      if (this.actionsDisplayChoice === 2 && this.actionsDisplayProblem !== null) {
        this.actionsDisplayChoice = 1;
      }
      this.sendSocketUpdate();
    },
    /** Problem item was edited */
    problemEdited(problem) {
      const id = Number(problem.id);
      const gwClItem = Number(problem.gwClItem);
      if (!gwClItem) {
        const pb = this.findPbWithId(id);
        pb.lWhat = problem.lWhat;
        pb.impact = problem.impact;
        pb.affectTeam = problem.affectTeam;
      }
      this.sendSocketUpdate();
    },
    /** Display the problem reporting editor */
    editProblem(problem) {
      this.$router.push({
        path: this.getPbReportingLink(problem.id),
        query: { pbReportingGwData: this.pbReportData },
      });
    },
    findPbWithId(pbId) {
      pbId = Number(pbId);
      for (let i = 0, len = this.gembaWalk.steps.length; i < len; i++) {
        const step = this.gembaWalk.steps[i];
        for (let j = 0, jlen = step.problems.length; j < jlen; j++) {
          const pb = step.problems[j];
          if (Number(pb.id) === pbId) {
            return pb;
          }
        }
      }
      return null;
    },
    /** Ask confirmation to delete the action */
    askDeleteAction(aIndex, action) {
      if (!this.canEdit) {
        return;
      }
      calert(this.$t('message.CONFIRM_DELETE_ACTION'), [
        {
          title: this.$t('message.DELETE'),
          action: () => this.deleteAction(aIndex, action),
          sClass: 'red',
        },
        {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
    /**
     * Delete the action
     * @param aIndex
     */
    deleteAction(aIndex, action) {
      const params = { gwId: this.compGembawalk.id, stepId: action.gwStep, id: action.id };
      this.$http.post('/server/gembaWalks/deleteAction.php', params).then(({ data }) => {
        if (data === 1) {
          toastr.success(this.$t('message.ACTION_DELETED'));
          this.sendSocketUpdate();
          this.currentStep.actions.splice(aIndex, 1);
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Edited actions display choice */
    actionChoiceMade() {
      if (this.actionsDisplayChoice === 2) {
        // Reset and prompt problem choice
        this.actionsDisplayProblem = null;
        this.actionsDisplayPbId = 0;
        this.$refs.apChoice.edit();
      } else {
        this.prevActionDispChoice = this.actionsDisplayChoice;
      }
    },
    actionPbChoiceMade() {
      for (let i = 0, len = this.displayedProblems.length; i < len; i++) {
        if (Number(this.displayedProblems[i].id) === Number(this.actionsDisplayPbId)) {
          this.actionsDisplayProblem = this.displayedProblems[i];
          return;
        }
      }
      this.actionsDisplayChoice = this.prevActionDispChoice;
    },
    /**
     * Problems data available to edit action's problem
     * @param {Object} action - Action object
     * @return {Array} Problem list
     */
    actionItemsProblems(action) {
      let problems = [];
      const pbStep = this.getStep(action.gwStep);
      if (pbStep !== null) {
        problems = this.stepProblems(pbStep).concat(this.stepChecklistProblems(pbStep));
      }
      problems.unshift({ id: 0, lWhat: this.$t('message.GW_NO_PB_SELECTED') });
      return problems;
    },
    /**
     * Add an action to the currently selected problem, or to the current GW step
     */
    addAction() {
      let team;
      if (
        this.actionsDisplayChoice !== 2
        || this.actionsDisplayProblem === null
        || this.actionsDisplayProblem.affectTeam === 0
      ) {
        team = this.currentStep.auditedTeam || 0;
        if (team === 0) {
          // No team defined for this step or this problem. The action must have a reportTeam
          this.displayTeamPicker = true;
          return;
        }
      } else {
        team = this.actionsDisplayProblem.affectTeam;
      }
      this.createAction(team, this.actionsDisplayProblem);
    },
    /**
     * When adding an action, if no team is defined in template, user chooses a reportTeam
     * @param {Array} teams - Array of team ids (should be only one team since singleValue is true)
     */
    teamPicked(teams) {
      this.displayTeamPicker = false;
      if (teams && teams.length) {
        this.createAction(teams[0]);
      }
    },
    /**
     * Create a new action
     * @param {String} teamId - Team id that will associated to the action
     */
    createAction(teamId, problem = null) {
      const params = {
        gwId: this.gembaWalk.id,
        stepId: this.currentStep.id,
        team: teamId,
        problem: problem ? problem.id : null,
      };
      this.$http.post('/server/gembaWalks/addAction.php', params).then((response) => {
        if (response && response.data instanceof Object && response.data.id) {
          this.currentStep.actions.push(response.data);
          this.sendSocketUpdate();
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /**
     * Validate or unvalidate a checklist item
     * @param {Object} item - Checklist item object
     */
    tickItem(item) {
      const ok = item.ok === 0 ? 1 : 0;
      this.$store.dispatch(`gembaWalks/${TICK_ITEM}`, { id: item.id, ok }).then((data) => {
        if (data && data instanceof Object && data.id) {
          this.sendSocketUpdate();
        }
      });
    },
    /**
     * Return the step with provided id
     * @param {Number} withId - Id of the step
     * @return {(Object|Null)} A step object, or null if no id is corresponding to a step
     */
    getStep(withId) {
      withId = Number(withId);
      for (let i = 0, len = this.gembaWalk.steps.length; i < len; i++) {
        if (Number(this.gembaWalk.steps[i].id) === withId) {
          return this.gembaWalk.steps[i];
        }
      }
      return null;
    },
    /** Return the problems linked to the step and not linked to the step's checklists */
    stepProblems(step) {
      return cloneDeep(step.problems);
    },
    /** Return the problems linked to the step's checklists */
    stepChecklistProblems(step) {
      const problems = [];
      (step.checklists || []).forEach((cl) => {
        cl.items.forEach((item) => {
          problems.push(...item.problems);
        });
      });
      return problems;
    },
    /** Send a socket msg to notify teams that this GW has been edited */
    sendSocketUpdate() {
      this.sendSocketMessage({ socketCompany: 1, id: this.gembaWalk.id, msg: SocketMessages.CC_MSGS.GW_STEP });
    },
    createHelpSteps() {
      this.demoGw = cloneDeep(IAHelp.demoOngoingGW());
      this.demoGw.completed = 1;
      this.demoGw.steps[0].checklists[0].items.forEach((item, i) => {
        item.ok = i % 2;
      });
      if (this.viewMode === 1) {
        this.$nextTick(() => { this.viewMode = 0; });
      }

      return [
        new IAHelpStep(null, this.$t('help.GW_COMPLETED_INTRO')),
        new IAHelpStep('.gw-completed .gw-synthesis', this.$t('help.GW_COMPLETED_SUMMARY'), (e, next) => {
          this.$refs.stepItems[0].itemExpand();
          this.$nextTick(next);
          return true;
        }),
        new IAHelpStep('.gw-completed .gw-checklist-item', this.$t('help.GW_COMPLETED_SUMMARY_ITEM')),
        new IAHelpStep('.gw-completed .switcher', this.$t('help.GW_COMPLETED_SWITCH'), () => {
          this.viewMode = 1;
        }),
        new IAHelpStep('.gw-completed .gw-completed__steps', this.$t('help.GW_COMPLETED_DETAILED_STEPS')),
        new IAHelpStep('.gw-completed__right-panel', this.$t('help.GW_COMPLETED_PROBLEMS_ACTIONS')),
        new IAHelpStep('.gw-completed__problems__add-problem', this.$t('help.GW_COMPLETED_ADD_PROBLEM'), () => {
          this.demoGw.steps[0].problems.push({
            affectTeam: this.currentTeam.id,
            created: this.$moment().format('YYYY-MM-DD'),
            gwStep: this.demoGw.steps[0].id,
            id: 1,
            impact: 0,
            lWhat: '',
          });
        }),
        new IAHelpStep('.problem-item', this.$t('help.GW_COMPLETED_EDIT_PROBLEM')),
        new IAHelpStep('.gw-completed__problems', this.$t('help.GW_COMPLETED_PROBLEMS')),
        new IAHelpStep('.gw-completed__actions__add-action', this.$t('help.GW_COMPLETED_ACTION_ADD')),
      ];
    },
    toggleModal(event, gw) {
      if (this.participatingTeamsModal) {
        this.modalContent = null;
      } else {
        gw.teamName = this.currentTeam.name;
        this.modalContent = { data: event, gw };
      }
      this.participatingTeamsModal = !this.participatingTeamsModal;
    },
    openTeamRecapModal(gw) {
      const gwId = gw.id;
      this.$http.post('/server/gembaWalks/getParticipants.php', { gwId })
        .then((response) => {
          const { data } = response;
          this.toggleModal(data, gw);
        }).catch(() => {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        });
    },
    sheetClicked(action) {
      this.$router.push(this.getActionCreationLink(action.id));
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.gw-completed {

  &__subheader {
    display: flex;
    justify-content: space-between;
    align-items: center;

    div:not(:last-child) {
      margin-right: 15px;
    }
  }

  &__dropdown {
    border: none;
    background-color: $color-gray-background;

    .dropdown-component__content {
      background-color: $color-gray-background;

      .router-link-active {
        color: white;
        font-weight: bold;
        background-color: $color-shizen-blue;
      }
    }
  }

  &__title {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__participant {
    display: flex;
    align-items: center;
    padding: 0 15px;
    padding-top: 15px;
    padding-bottom: 15px;
    border: none;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  &__global-graph {
    margin: 2rem 0;
  }

  &__expendable-step {
    margin-top: 15px;
    background-color: white;
    border-radius: $main-border-radius;

    .settings-item {
      border-radius: $main-border-radius;
    }

    &:last-child {
      margin-bottom: 30px;
    }
  }

  .switcher {
    height: 2.3rem;
  }

  &__details {
    display: flex;
  }

  &__steps, &__problems, &__actions {
    background: white;
    border-radius: $main-border-radius;

    h2.basic-sub-title {
      margin-top: 0;
    }
  }

  &__steps, &__right-panel {
    flex: 1;
    margin-top: $page-content-padding;
  }

  &__steps {
    margin-right: $page-content-padding;
    padding: 0.9rem;
  }

  &__right-panel {
    margin-top: 0.7rem;
    flex: 1;
  }

  &__problems {
    padding: 0.9rem;
    margin-bottom: 0.7rem;

    &__add-problem {
      margin: 20px 0;
    }

    .dropdown-component {
      margin-bottom: 30px;
    }

    .problem-item {
      border: 1px solid $color-gray-border;
    }
  }

  &__actions {
    margin-top: $page-content-padding;
    padding: 0.9rem;

    &__add-action {
      margin-top: 20px;
    }

    .dropdown-component {
      margin-bottom: 30px;
    }

    .pending-action-item {
      border: 1px solid $color-gray-border;
    }
  }

  .gw-synthesis {
    overflow: auto;
  }

  .list__tile--link {
    padding: 0px 16px;
  }

  .radio-group {
    margin-left: .5em;
  }

  .review-comment {
    border: 2px solid #B0B0B0;
    padding: 2px 5px;
    margin-top: 2px;
    min-height: 1.5em;
  }

  .split-mid {

    h3 {
      font-weight: 600;
      text-align: center;
      padding: 0px 20px;
      margin-top: 15px;
    }

    .no-problems-message {
      margin: 5px 10px;
    }
  }

  .settings-dropdown .summary-comments {
    padding: 5px 16px;

    .settings-item {
      padding: 0px;

      svg {
        margin-left: 15px;
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .gw-completed {

    .settings-dropdown {

      .gw-line-chart {
        width: calc(100% - 2em);
      }
    }

    &__details {
      flex-direction: column;
    }

    .gw-completed-steps {
      margin-right: 0;
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .gw-completed {

    &__participant {
      display: none;
    }

    .gw-sr-checklists {
      width: 100%;
    }

    .gw-completed-steps {
      overflow-y: visible;
    }
  }
}
</style>
